import { useMutation } from "@apollo/client";
import ModeEdit from "@mui/icons-material/ModeEdit";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Grid,
  LinearProgress,
  Stack,
  Typography,
} from "@mui/material";
import { FeatureFlagKey } from "encamp-shared/src/featureFlags";
import {
  EpisodicEventItemFragment,
  WasteEpisodicEventStatus,
  WasteEpisodicEventType,
} from "generated-graphql/graphql";
import { useFeatureFlags } from "hooks/useFeatureFlags";
import { useTenant } from "hooks/useTenant";
import { ReactNode } from "react";
import { Link as RouterLink } from "react-router-dom";

import { formatContainerDate } from "../Containers/containerDisplay";
import { CREATE_EPISODIC_EVENT_SUBMISSION_MUTATION } from "./api";
import {
  daysUsedInWindow,
  episodicSubmissionRoute,
  EVENT_STATUS_LABELS,
  EVENT_TYPE_LABELS,
  EVENT_WINDOW_DAYS,
  filedSubmission,
  formatEventWindow,
  isEventWindowLapsed,
  openSubmission,
  resolveNotificationCellState,
} from "./episodicDisplay";
import { EventContainersSection } from "./EventContainersSection";

const EVENT_STATUS_CHIP_COLOR: Record<WasteEpisodicEventStatus, "warning" | "success" | "default"> =
  {
    [WasteEpisodicEventStatus.Active]: "warning",
    [WasteEpisodicEventStatus.Closed]: "success",
    [WasteEpisodicEventStatus.Expired]: "default",
  };

const EVENT_BASIS_CITATION = "40 CFR 262 subpart L";

function EventFieldValue({ label, children }: { label: string; children: ReactNode }) {
  return (
    <Grid size={{ xs: 12, sm: 6, md: 3 }}>
      <Typography variant="caption" color="text.secondary" display="block">
        {label}
      </Typography>
      <Typography variant="body2">{children}</Typography>
    </Grid>
  );
}

/** The days-used-of-60 progress bar, with the ship-by deadline right-aligned
 * under it. Turns red once the window has lapsed. */
function WindowProgress({ event, lapsed }: { event: EpisodicEventItemFragment; lapsed: boolean }) {
  const daysUsed = daysUsedInWindow(event.startDate);
  return (
    <Box>
      <LinearProgress
        variant="determinate"
        value={(daysUsed / EVENT_WINDOW_DAYS) * 100}
        color={lapsed ? "error" : "primary"}
        sx={{ mb: 0.5, height: 6, borderRadius: 1 }}
      />
      <Stack direction="row" justifyContent="space-between" flexWrap="wrap" gap={1}>
        <Typography variant="caption" color="text.secondary">
          {daysUsed} of {EVENT_WINDOW_DAYS} days used
        </Typography>
        <Typography variant="caption" color="text.secondary">
          Ship &amp; remove waste by {formatContainerDate(event.accumulationDeadline)}
        </Typography>
      </Stack>
    </Box>
  );
}

/**
 * The EPA-notification handoff banner, in the same filed/open/none priority
 * as the ledger's Notification cell (`resolveNotificationCellState`) so the
 * card never disagrees with the table row for the same event. A completed
 * filing links to the submission that filed it; a manually recorded
 * notification (no submission at all) shows its date/form reference instead,
 * since there's no submission to link to.
 */
function NotificationBanner({
  event,
  tenantId,
  stepperEnabled,
}: {
  event: EpisodicEventItemFragment;
  tenantId: string;
  stepperEnabled: boolean;
}) {
  const [createSubmission, { loading }] = useMutation(CREATE_EPISODIC_EVENT_SUBMISSION_MUTATION, {
    refetchQueries: ["FacilityEpisodicEvents"],
  });

  const state = resolveNotificationCellState(event);

  if (state.kind === "filed") {
    const filed = filedSubmission(event);
    return (
      <Alert severity="success">
        <Stack direction="row" alignItems="center" spacing={1.5} flexWrap="wrap">
          <span>EPA notified</span>
          {filed && (
            <Button
              component={RouterLink}
              to={episodicSubmissionRoute(tenantId, filed.id, stepperEnabled)}
              target="_blank"
              rel="noopener"
              size="small"
            >
              View submission
            </Button>
          )}
        </Stack>
      </Alert>
    );
  }

  if (state.kind === "notifiedEpa") {
    return (
      <Alert severity="success">
        EPA notified · {formatContainerDate(state.notifiedEpaAt)}
        {state.epaFormReference ? ` · ${state.epaFormReference}` : ""}
      </Alert>
    );
  }

  if (state.kind === "submissionInProgress") {
    const open = openSubmission(event);
    return (
      <Alert
        severity="info"
        action={
          open && (
            <Button
              component={RouterLink}
              to={episodicSubmissionRoute(tenantId, open.id, stepperEnabled)}
              target="_blank"
              rel="noopener"
              size="small"
            >
              View submission
            </Button>
          )
        }
      >
        Notification submission in progress
      </Alert>
    );
  }

  const handleCreateSubmission = async () => {
    const result = await createSubmission({ variables: { eventId: event.id } });
    const submissionId = result.data?.createEpisodicEventSubmission;
    if (submissionId) {
      // New tab so the event page (and its manual-testing context) stays put;
      // the refetch above keeps this page's banner state current regardless.
      window.open(episodicSubmissionRoute(tenantId, submissionId, stepperEnabled), "_blank");
    }
  };

  const dueNote = event.type === WasteEpisodicEventType.Unplanned ? " (due within 72 hours)" : "";

  return (
    <Alert
      severity="warning"
      action={
        <Button size="small" disabled={loading} onClick={handleCreateSubmission}>
          Create submission
        </Button>
      }
    >
      <Typography variant="body2" sx={{ fontWeight: 600 }}>
        Notification not filed{dueNote} — EPA Form 8700-12
      </Typography>
      <Typography variant="body2">
        Logging an episodic event doesn&apos;t file it. Create a submission to notify your
        regulator; the filing is tracked as a task.
      </Typography>
    </Alert>
  );
}

/**
 * Detail card for one episodic event of any status: window, removal deadline,
 * on-site cap, the EPA-notification state, and the event's own container
 * list. ACTIVE events additionally get the days-used progress bar, the
 * lapsed-window alert, and the Edit / Close actions — a closed or expired
 * event's window is settled, so those disappear rather than render disabled.
 * The notification banner stays for every status: an unfiled notification is
 * still an obligation after the window ends.
 */
export function EpisodicEventCard({
  event,
  onEdit,
  onClose,
}: {
  event: EpisodicEventItemFragment;
  onEdit?: () => void;
  onClose?: () => void;
}) {
  const { tenantId } = useTenant();
  const { featureFlags } = useFeatureFlags();
  const stepperEnabled = featureFlags[FeatureFlagKey.WasteSubmissionStepper] === true;
  const isActive = event.status === WasteEpisodicEventStatus.Active;
  const lapsed = isActive && isEventWindowLapsed(event.accumulationDeadline);

  return (
    // Matches the container detail page's SectionCard chrome: a flat white
    // tile sitting on the page's canvas background.
    <Card elevation={0}>
      <CardContent sx={{ p: 3 }}>
        {/* Roomier than the default form rhythm: these cards are the whole
            page, so the sections can afford to breathe. */}
        <Stack spacing={3.5}>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="flex-start"
            flexWrap="wrap"
            gap={1.5}
          >
            <Box>
              <Typography variant="h6" sx={{ mb: 0.5 }}>
                {event.reason}
              </Typography>
              <Stack direction="row" spacing={1} flexWrap="wrap">
                <Chip
                  size="small"
                  color={EVENT_STATUS_CHIP_COLOR[event.status]}
                  label={EVENT_STATUS_LABELS[event.status]}
                />
                <Chip size="small" label={EVENT_TYPE_LABELS[event.type]} />
                <Chip size="small" label={`${event.containers.length} containers`} />
                {lapsed && <Chip size="small" color="error" label="Window ended" />}
              </Stack>
            </Box>
            {isActive && (
              <Stack direction="row" spacing={1}>
                <Button size="small" startIcon={<ModeEdit />} onClick={onEdit} disabled={!onEdit}>
                  Edit
                </Button>
                <Button size="small" onClick={onClose} disabled={!onClose}>
                  Close event
                </Button>
              </Stack>
            )}
          </Stack>

          <Grid container spacing={2} rowSpacing={2.5}>
            <EventFieldValue label="Event window">
              {formatEventWindow(event.startDate, event.endDate)}
            </EventFieldValue>
            <EventFieldValue label="Removal deadline">
              {formatContainerDate(event.accumulationDeadline)}
            </EventFieldValue>
            {/* Only rendered while the event is ACTIVE: the exemption is live
                behavior, and a closed or expired event has none to describe.
                The cap exemption ends at the deadline even while the status
                is still ACTIVE (the sweep hasn't run yet), so a lapsed card
                must not claim the waste is exempt. */}
            {isActive && !lapsed && (
              <EventFieldValue label="On-site cap">
                Episodic waste exempt · status unchanged
              </EventFieldValue>
            )}
            {isActive && lapsed && (
              <EventFieldValue label="On-site cap">
                {`Exemption ended ${formatContainerDate(
                  event.accumulationDeadline
                )} · remove remaining waste`}
              </EventFieldValue>
            )}
            <EventFieldValue label="Basis">{EVENT_BASIS_CITATION}</EventFieldValue>
          </Grid>

          {isActive && <WindowProgress event={event} lapsed={lapsed} />}

          {lapsed && (
            <Alert severity="error">
              The 60-day window has ended — ship any remaining waste and close the event.
            </Alert>
          )}

          <NotificationBanner
            event={event}
            tenantId={tenantId ?? ""}
            stepperEnabled={stepperEnabled}
          />

          <EventContainersSection event={event} />
        </Stack>
      </CardContent>
    </Card>
  );
}
